import { BaseEdge, EdgeLabelRenderer, type EdgeProps } from "@xyflow/react";
import { Dropdown, Tooltip, theme } from "antd";
import { AuditOutlined, FileTextOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useCanvas } from "./CanvasContext";
import { channelX } from "./layout";

/**
 * The spine between two steps, carrying the control that inserts a new one.
 *
 * "Add step" used to be a button at the bottom of a list, which added the step
 * at the end and left the person to drag it to where they meant. Putting a `+`
 * in each gap says where the step will go by *being* there -- the commonest
 * reason to add a step is that something is missing in the middle, and this is
 * the affordance that a canvas has and a list does not.
 */
export const InsertEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  data,
}: EdgeProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const canvas = useCanvas();
  const at = (data as { insertAt?: number } | undefined)?.insertAt;

  // A straight vertical run; the two ends share an x on the spine.
  const path = `M ${sourceX},${sourceY} L ${targetX},${targetY}`;
  const midY = (sourceY + targetY) / 2;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{ stroke: token.colorBorder, strokeWidth: 1.5 }}
      />
      {at !== undefined && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${targetX}px, ${midY}px)`,
              pointerEvents: "all",
              // Above the edge it sits on. Without this the line's own <path>
              // wins the hit test at exactly the point the control occupies,
              // so the "+" looks live and does nothing when clicked.
              zIndex: 6,
            }}
          >
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "DataEntry",
                    icon: <FileTextOutlined />,
                    label: t("A step people fill in"),
                    onClick: () => canvas.onAddStage("DataEntry", at),
                  },
                  {
                    key: "Approval",
                    icon: <AuditOutlined />,
                    label: t("A step people sign off"),
                    onClick: () => canvas.onAddStage("Approval", at),
                  },
                ],
              }}
            >
              <Tooltip title={t("Insert a step here")}>
                <button
                  aria-label={t("Insert a step here")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    padding: 0,
                    borderRadius: "50%",
                    cursor: "pointer",
                    border: `1px dashed ${token.colorBorder}`,
                    background: token.colorBgContainer,
                    color: token.colorTextTertiary,
                    opacity: 0.55,
                    transition: "opacity .15s, border-color .15s, color .15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.borderColor = token.colorPrimary;
                    e.currentTarget.style.color = token.colorPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.55";
                    e.currentTarget.style.borderColor = token.colorBorder;
                    e.currentTarget.style.color = token.colorTextTertiary;
                  }}
                >
                  <PlusOutlined style={{ fontSize: 11 }} />
                </button>
              </Tooltip>
            </Dropdown>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

/**
 * The path a case takes when it *skips* an optional step.
 *
 * Routed by hand into an explicit channel to the left of the spine rather than
 * left to React Flow's automatic routing. Two optional steps next to each other
 * -- an HSE assessment and an environmental assessment, each with its own
 * trigger -- produce two bypasses that start and end within a few pixels of one
 * another; automatic routing draws them on top of each other, and the picture
 * then cannot say whether one, the other, or both were skipped. Giving each its
 * own channel is what makes a real branchy process readable.
 */
export const BypassEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  markerEnd,
  data,
}: EdgeProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { channel = 0, skipped } = (data ?? {}) as {
    channel?: number;
    skipped?: boolean;
  };

  const x = channelX(channel);
  const r = 10;
  // Out of the spine, down the channel, back to the spine — with rounded
  // corners so it reads as a route rather than a bracket.
  const path = [
    `M ${sourceX},${sourceY}`,
    `L ${x + r},${sourceY}`,
    `Q ${x},${sourceY} ${x},${sourceY + r}`,
    `L ${x},${targetY - r}`,
    `Q ${x},${targetY} ${x + r},${targetY}`,
    `L ${targetX},${targetY}`,
  ].join(" ");

  // When a simulation says this step is skipped, the bypass is the path the
  // case actually takes — so it stops being the faint alternative.
  const stroke = skipped ? token.colorPrimary : token.colorTextQuaternary;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke,
          strokeWidth: skipped ? 2 : 1,
          strokeDasharray: skipped ? undefined : "5 4",
        }}
      />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${x}px, ${
              (sourceY + targetY) / 2
            }px)`,
            padding: "1px 6px",
            borderRadius: 999,
            fontSize: 10,
            whiteSpace: "nowrap",
            background: token.colorBgContainer,
            color: skipped ? token.colorPrimary : token.colorTextTertiary,
            border: `1px solid ${
              skipped ? token.colorPrimaryBorder : token.colorBorderSecondary
            }`,
          }}
        >
          {skipped ? t("skipped") : t("if not")}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export const edgeTypes = { insert: InsertEdge, bypass: BypassEdge };
